import { Link } from 'react-router-dom';
import { clsx } from 'clsx';
import {
    LEGAL_COMPANY_NAME,
    LEGAL_CONTACT_EMAIL,
    LEGAL_COPYRIGHT_YEAR,
} from '../legal/policyVersion';

/**
 * Site-wide legal chrome (GitHub-style bar, Outris LE branding).
 * No cookie preference center / CCPA “Do not sell” — this product uses login
 * legal consent and session cookies only (see Privacy §7).
 */
const LINKS: Array<{ label: string; to?: string; href?: string }> = [
    { label: 'Documentation', to: '/documentation' },
    { label: 'Terms', to: '/terms' },
    { label: 'Privacy', to: '/privacy' },
    { label: 'Contact', href: `mailto:${LEGAL_CONTACT_EMAIL}` },
];

type OutrisSiteFooterProps = {
    /** Pin to the viewport bottom (login). Other shells keep the bar in document flow. */
    pinned?: boolean;
};

/**
 * Full-width site chrome: copyright on the left, legal nav on the right.
 * Use on document pages and other non-canvas shells — not on Workbench / fusion canvases.
 */
export function OutrisSiteFooter({ pinned = false }: OutrisSiteFooterProps) {
    return (
        <footer
            className={clsx(
                'border-t border-border bg-background',
                pinned ? 'fixed inset-x-0 bottom-0 z-20' : 'mt-auto',
            )}
            data-testid="outris-site-footer"
            aria-label="Footer navigation"
        >
            <div className="flex w-full flex-col gap-3 px-6 py-4 sm:flex-row sm:items-center sm:justify-between sm:gap-8 sm:px-10 text-xs text-muted-foreground">
                <div className="shrink-0 leading-snug" data-testid={pinned ? 'login-footer-copyright' : undefined}>
                    <p>
                        © {LEGAL_COPYRIGHT_YEAR} {LEGAL_COMPANY_NAME}
                    </p>
                </div>
                <nav
                    className="flex flex-wrap items-center gap-x-5 gap-y-2 sm:justify-end sm:gap-x-6"
                    aria-label="Footer"
                >
                    {LINKS.map((item) =>
                        item.to ? (
                            <Link key={item.label} to={item.to} className="hover:text-foreground hover:underline">
                                {item.label}
                            </Link>
                        ) : (
                            <a
                                key={item.label}
                                href={item.href}
                                className="hover:text-foreground hover:underline"
                                {...(item.href?.startsWith('http')
                                    ? { target: '_blank', rel: 'noreferrer' }
                                    : {})}
                            >
                                {item.label}
                            </a>
                        ),
                    )}
                </nav>
            </div>
        </footer>
    );
}
